import Button from '@/components/ui/button';
import {
  useCreateDeliveryPartner,
  useDeliveryPartnerFormOptions,
} from '@/framework/use-delivery-partner';
import type { CreateDeliveryPartnerInput } from '@/types/delivery-partner';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  FALLBACK_PERIODS,
  FALLBACK_REGIONS,
  FALLBACK_WEEK_DAYS,
  STEPS,
} from './constants';
import DeliveryPartnerSuccess from './delivery-partner-success';
import { onlyDigits } from './masks';
import {
  deliveryPartnerSchema,
  STEP_FIELDS,
  type DeliveryPartnerFormValues,
} from './schema';
import StepDadosPessoais from './steps/step-dados-pessoais';
import StepDadosProfissionais from './steps/step-dados-profissionais';
import StepPreferencias from './steps/step-preferencias';
import TurnstileWidget from './turnstile-widget';

const TURNSTILE_ENABLED = Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY);

const DEFAULT_VALUES: Partial<DeliveryPartnerFormValues> = {
  regions: [],
  available_days: [],
  preferred_periods: [],
  accepted_terms: false,
  accepted_safety: false,
};

export default function DeliveryPartnerForm() {
  const [step, setStep] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const { options } = useDeliveryPartnerFormOptions();
  const { createDeliveryPartner, isLoading, isSuccess, data, errorMessage } =
    useCreateDeliveryPartner();

  const methods = useForm<DeliveryPartnerFormValues>({
    resolver: yupResolver(deliveryPartnerSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES as DeliveryPartnerFormValues,
  });

  if (isSuccess) {
    return <DeliveryPartnerSuccess protocol={data?.id} />;
  }

  const isLastStep = step === STEPS.length - 1;
  const missingChallenge = TURNSTILE_ENABLED && !turnstileToken;

  const goNext = async () => {
    const valid = await methods.trigger(STEP_FIELDS[step] as never, {
      shouldFocus: true,
    });

    if (valid) {
      setStep((current) => Math.min(current + 1, STEPS.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    setStep((current) => Math.max(current - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSubmit = (values: DeliveryPartnerFormValues) => {
    createDeliveryPartner(toApiPayload(values, turnstileToken));
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        noValidate
        className="mx-auto max-w-3xl"
      >
        <StepIndicator current={step} />

        <div className="rounded-3xl border border-border-base bg-light p-6 sm:p-8">
          {step === 0 && <StepDadosPessoais />}

          {step === 1 && (
            <StepDadosProfissionais banks={options?.banks ?? []} />
          )}

          {step === 2 && (
            <>
              <StepPreferencias
                regions={options?.regions ?? FALLBACK_REGIONS}
                weekDays={options?.week_days ?? FALLBACK_WEEK_DAYS}
                periods={options?.periods ?? FALLBACK_PERIODS}
              />
              <TurnstileWidget onVerify={setTurnstileToken} />
            </>
          )}

          {errorMessage && (
            <p
              role="alert"
              className="mt-6 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600"
            >
              {errorMessage}
            </p>
          )}

          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            {step > 0 ? (
              <Button variant="outline" onClick={goBack} disabled={isLoading}>
                Voltar
              </Button>
            ) : (
              <span className="hidden sm:block" />
            )}

            {isLastStep ? (
              <Button
                type="submit"
                loading={isLoading}
                disabled={isLoading || missingChallenge}
              >
                Enviar candidatura
              </Button>
            ) : (
              <Button onClick={goNext}>Continuar</Button>
            )}
          </div>

          {missingChallenge && isLastStep && (
            <p className="mt-3 text-center text-xs text-body sm:text-right">
              Conclua a verificação de segurança para enviar.
            </p>
          )}
        </div>
      </form>
    </FormProvider>
  );
}

function StepIndicator({ current }: { current: number }) {
  return (
    <ol className="mb-6 flex items-center gap-2" aria-label="Etapas do cadastro">
      {STEPS.map((item, index) => {
        const isActive = index === current;
        const isDone = index < current;

        return (
          <li key={item.id} className="flex flex-1 flex-col gap-2">
            <span
              className={cn(
                'h-1.5 w-full rounded-full transition-colors',
                isActive || isDone ? 'bg-accent' : 'bg-border-base',
              )}
            />
            <span
              aria-current={isActive ? 'step' : undefined}
              className={cn(
                'text-xs font-medium',
                isActive ? 'text-accent' : 'text-body',
              )}
            >
              {item.title}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function toApiPayload(
  values: DeliveryPartnerFormValues,
  turnstileToken: string | null,
): CreateDeliveryPartnerInput {
  return {
    first_name: values.first_name,
    last_name: values.last_name,
    cpf: onlyDigits(values.cpf),
    rg: values.rg || undefined,
    phone: onlyDigits(values.phone),
    email: values.email,
    birth_date: values.birth_date,
    modalidade: values.modalidade as CreateDeliveryPartnerInput['modalidade'],
    cnpj: values.cnpj ? onlyDigits(values.cnpj) : undefined,
    has_smartphone: values.has_smartphone,
    has_app: values.has_app,
    accepts_contact: values.accepts_contact,
    bank_name: values.bank_name,
    bank_code: values.bank_code || undefined,
    account_type:
      values.account_type as CreateDeliveryPartnerInput['account_type'],
    bank_agency: onlyDigits(values.bank_agency),
    bank_account: values.bank_account,
    has_pix: values.has_pix,
    pix_key_type: values.has_pix
      ? (values.pix_key_type as CreateDeliveryPartnerInput['pix_key_type'])
      : undefined,
    pix_key: values.has_pix ? values.pix_key : undefined,
    regions: values.regions,
    available_days: values.available_days,
    preferred_periods: values.preferred_periods,
    accepted_terms: values.accepted_terms,
    accepted_safety: values.accepted_safety,
    ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
  };
}
